import React from 'react'
import { NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Server,
  Activity,
  FileText,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Fingerprint,
  Globe,
  HardDrive,
  Blocks,
  Box,
  Shield,
  KeyRound,
  Key,
  Settings,
  Database,
  DollarSign,
  ShieldCheck,
  RefreshCw,
  BarChart3,
  AlertTriangle,
  Siren,
  SlidersHorizontal,
  LogOut,
} from 'lucide-react'
import { cn } from '@/core/infra_utils'
import { Button, Text } from '@agion/ui'
import { getConfig } from '@/core/infra_runtime_env'
import { InfraOrgSwitcher } from '@/components/InfraOrgSwitcher'
import { InfraUiSwitcher } from '@/components/InfraUiSwitcher'
import { usePlatformAdmin } from '@/hooks/infra_use_platform_admin'
import AgionLogo from '@/assets/agion-logo.svg'
import AgionLogoDark from '@/assets/agion-logo-dark.svg'

interface NavItem {
  nameKey: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  children?: NavItem[]
  /** If true, item only visible to platform admins (#670) */
  platformAdminOnly?: boolean
}

const navigation: NavItem[] = [
  {
    nameKey: 'overview',
    icon: LayoutDashboard,
    children: [
      { nameKey: 'dashboard', href: '/', icon: LayoutDashboard },
    ],
  },
  {
    nameKey: 'infrastructure',
    icon: HardDrive,
    children: [
      { nameKey: 'environments', href: '/environments', icon: Server },
      { nameKey: 'operations', href: '/operations', icon: Activity },
      { nameKey: 'images', href: '/images', icon: HardDrive },
      { nameKey: 'charts', href: '/charts', icon: Box },
      { nameKey: 'terraformModules', href: '/terraform-modules', icon: Blocks },
    ],
  },
  {
    nameKey: 'security',
    icon: Shield,
    children: [
      { nameKey: 'oidcClients', href: '/oidc-clients', icon: Fingerprint },
      { nameKey: 'credentials', href: '/credentials', icon: KeyRound },
      { nameKey: 'sshKeys', href: '/ssh-keys', icon: Key },
      { nameKey: 'dns', href: '/dns', icon: Globe },
    ],
  },
  {
    nameKey: 'management',
    icon: Settings,
    children: [
      { nameKey: 'backups', href: '/backups', icon: Database },
      { nameKey: 'costs', href: '/costs', icon: DollarSign },
      { nameKey: 'compliance', href: '/compliance', icon: ShieldCheck },
      { nameKey: 'updates', href: '/updates', icon: RefreshCw },
    ],
  },
  {
    nameKey: 'platform',
    icon: BarChart3,
    children: [
      { nameKey: 'hosts', href: '/hosts', icon: Server, platformAdminOnly: true },
      { nameKey: 'alerts', href: '/alerts', icon: AlertTriangle, platformAdminOnly: true },
      { nameKey: 'rolloutControls', href: '/rollout-controls', icon: SlidersHorizontal, platformAdminOnly: true },
      { nameKey: 'incidents', href: '/incidents', icon: Siren },
      { nameKey: 'audit', href: '/audit', icon: FileText },
    ],
  },
]

const NavSection: React.FC<{
  item: NavItem
  collapsed: boolean
  isPlatformAdmin: boolean
}> = ({ item, collapsed, isPlatformAdmin }) => {
  const { t } = useTranslation('pages')
  const [isOpen, setIsOpen] = React.useState(true)

  // Hide platform-admin-only sections for non-admins
  if (item.platformAdminOnly && !isPlatformAdmin) {
    return null
  }

  // Filter children for platform-admin-only items
  const visibleChildren = item.children?.filter(
    child => !child.platformAdminOnly || isPlatformAdmin
  )

  const hasChildren = visibleChildren && visibleChildren.length > 0

  // Single item without children — direct link
  if (!hasChildren && item.href) {
    return (
      <NavLink
        to={item.href}
        end={item.href === '/'}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600',
            isActive
              ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
              : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/30'
          )
        }
        title={collapsed ? t(`nav.${item.nameKey}`) : undefined}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {!collapsed && <span className="flex-1">{t(`nav.${item.nameKey}`)}</span>}
      </NavLink>
    )
  }

  // Collapsed mode — just show child icons
  if (collapsed) {
    return (
      <div className="space-y-1">
        {visibleChildren?.map((child) => (
          <NavLink
            key={child.nameKey}
            to={child.href!}
            end={child.href === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center justify-center rounded-md p-2 transition-colors',
                isActive
                  ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100'
                  : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
              )
            }
            title={t(`nav.${child.nameKey}`)}
          >
            <child.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          </NavLink>
        ))}
      </div>
    )
  }

  // Expanded — segmented section with header + children
  return (
    <div className="space-y-0.5">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors h-auto"
      >
        <span className="flex-1 text-left">{t(`nav.${item.nameKey}`)}</span>
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </Button>

      {isOpen && visibleChildren && (
        <div className="space-y-0.5">
          {visibleChildren.map((child) => (
            <NavLink
              key={child.nameKey}
              to={child.href!}
              end={child.href === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600',
                  isActive
                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30'
                )
              }
            >
              <child.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">{t(`nav.${child.nameKey}`)}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const { t } = useTranslation('pages')
  const { isPlatformAdmin } = usePlatformAdmin()
  const isDark = document.documentElement.classList.contains('dark')

  const handleLogout = () => {
    const loginUrl = getConfig('VITE_LOGIN_URL', 'https://login.dev.agion.dev')
    window.location.href = `${loginUrl}/logout`
  }

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between h-14 pl-5 pr-3 mb-1">
        <div className="flex items-center gap-2.5">
          {!collapsed && (
            <img
              src={isDark ? AgionLogoDark : AgionLogo}
              alt="Agion"
              className="h-5 w-auto"
            />
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 transition-colors h-auto"
          aria-label={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-3" aria-label={t('sidebar.mainNavigation')}>
        {navigation.map((item) => (
          <NavSection key={item.nameKey} item={item} collapsed={collapsed} isPlatformAdmin={isPlatformAdmin} />
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700/50 p-2 space-y-1">
        {/* Org Switcher */}
        <InfraOrgSwitcher collapsed={collapsed} />
        <InfraUiSwitcher collapsed={collapsed} />

        {/* Logout */}
        <div
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer',
            collapsed && 'justify-center px-0'
          )}
          role="button"
          tabIndex={0}
          onClick={handleLogout}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLogout() } }}
        >
          <LogOut className="h-5 w-5 text-slate-600 dark:text-slate-400 flex-shrink-0" />
          {!collapsed && (
            <Text as="span" className="text-slate-700 dark:text-slate-300 text-sm">
              {t('nav.logout', 'Logout')}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
